import { z } from 'zod';
import { CURRENCY_CODES } from './money.js';
import { NON_ACCOUNT_METHODS } from './finance.js';
import { barcodeSchema } from './inventory-schemas.js';

/** Phase 4: expiry & batches, promotions, weighing, yields, wastage, variants, orders, deliveries. */

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z.uuid();
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-21');
const qty = z.number().int().positive('Quantity must be more than zero').max(1_000_000_000);
const cents = z.number().int().min(0).max(1_000_000_000_00);

/* ── Scale barcodes (weighing) ──────────────────────────────────────── */

export interface ScaleBarcodeConfig {
  /** Two-digit prefixes the scale prints (GS1 reserves 20–29 for in-store use). */
  prefixes: string[];
  /** Digits of product code (PLU) after the prefix. */
  pluLength: number;
  /** What the value digits hold: weight in grams, or price in cents. */
  mode: 'weight' | 'price';
}

export const DEFAULT_SCALE_CONFIG: ScaleBarcodeConfig = { prefixes: ['20', '21', '22', '23', '24', '25', '26', '27', '28', '29'], pluLength: 5, mode: 'weight' };

export const scaleConfigSchema = z.object({
  prefixes: z.array(z.string().regex(/^2\d$/, 'Scale prefixes are 20–29')).min(1).max(10),
  pluLength: z.number().int().min(4).max(6),
  mode: z.enum(['weight', 'price']),
});

/**
 * Reads an EAN-13 label printed by a shop scale: 2-digit prefix, PLU, value, check digit.
 * With a 5-digit PLU the value has 5 digits (grams or cents). Returns null if it isn't one.
 */
export function parseScaleBarcode(code: string, cfg: ScaleBarcodeConfig): { plu: string; weightGrams?: number; priceCents?: number } | null {
  if (!/^\d{13}$/.test(code)) return null;
  const prefix = code.slice(0, 2);
  if (!cfg.prefixes.includes(prefix)) return null;
  const digits = code.split('').map(Number);
  const check = digits.pop()!;
  const sum = digits.reverse().reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  if ((10 - (sum % 10)) % 10 !== check) return null;
  const plu = String(Number(code.slice(2, 2 + cfg.pluLength)));
  const value = Number(code.slice(2 + cfg.pluLength, 12));
  if (!value) return null;
  return cfg.mode === 'weight' ? { plu, weightGrams: value } : { plu, priceCents: value };
}

/* ── Promotions ─────────────────────────────────────────────────────── */

export const PROMOTION_KINDS = {
  percent_off: 'Percentage off',
  fixed_price: 'Special price',
  buy_x_get_y: 'Buy some, get some free',
} as const;

export const promotionSchema = z
  .object({
    name: z.string().trim().min(3, 'Give the promotion a name customers will see').max(60),
    kind: z.enum(['percent_off', 'fixed_price', 'buy_x_get_y']),
    percentBp: z.number().int().min(1).max(9000).nullable().optional(),
    fixedPriceCents: cents.nullable().optional(),
    buyQty: z.number().int().min(1).max(100).nullable().optional(),
    getQty: z.number().int().min(1).max(100).nullable().optional(),
    startsOn: dateOnly,
    endsOn: dateOnly,
    branchIds: z.array(id).max(50).default([]),
    productIds: z.array(id).max(500).default([]),
    categoryIds: z.array(id).max(50).default([]),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.endsOn >= v.startsOn, { message: 'The end date is before the start date', path: ['endsOn'] })
  .refine((v) => v.productIds.length + v.categoryIds.length > 0, { message: 'Choose the products or categories it applies to', path: ['productIds'] })
  .refine((v) => v.kind !== 'percent_off' || !!v.percentBp, { message: 'Enter the percentage', path: ['percentBp'] })
  .refine((v) => v.kind !== 'fixed_price' || v.fixedPriceCents != null, { message: 'Enter the special price', path: ['fixedPriceCents'] })
  .refine((v) => v.kind !== 'buy_x_get_y' || (!!v.buyQty && !!v.getQty), { message: 'Enter how many to buy and how many are free', path: ['buyQty'] });
export type PromotionInput = z.infer<typeof promotionSchema>;

/* ── Wastage ────────────────────────────────────────────────────────── */

export const WASTAGE_REASONS = {
  trim: 'Trim and fat',
  spoilage: 'Spoiled',
  expired: 'Past expiry date',
  damaged: 'Damaged',
  drip_loss: 'Drip and moisture loss',
  sample: 'Tasting or samples',
  other: 'Other',
} as const;
export type WastageReason = keyof typeof WASTAGE_REASONS;

export const wastageSchema = z.object({
  branchId: id,
  reason: z.enum(Object.keys(WASTAGE_REASONS) as [WastageReason, ...WastageReason[]]),
  note: nullableText(300),
  lines: z.array(z.object({ productId: id, qtyMilli: qty, batchId: id.nullable().optional() })).min(1, 'Add at least one product').max(200),
});

/* ── Carcass breakdown (yields) ─────────────────────────────────────── */

export const breakdownSchema = z
  .object({
    branchId: id,
    sourceProductId: id,
    sourceQtyMilli: qty,
    note: nullableText(300),
    outputs: z.array(z.object({ productId: id, qtyMilli: qty })).min(1, 'Add the cuts produced').max(60),
  })
  .refine((v) => v.outputs.reduce((a, o) => a + o.qtyMilli, 0) <= v.sourceQtyMilli, {
    message: 'The cuts weigh more than what was cut up',
    path: ['outputs'],
  })
  .refine((v) => !v.outputs.some((o) => o.productId === v.sourceProductId), { message: 'A cut cannot be the carcass itself', path: ['outputs'] });
export type BreakdownInput = z.infer<typeof breakdownSchema>;

/* ── Variants ───────────────────────────────────────────────────────── */

export const variantGroupSchema = z.object({
  name: z.string().trim().min(2).max(120),
  sku: z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .max(24)
    .regex(/^[A-Z0-9\-_.]+$/),
  categoryId: id.nullable().optional(),
  unit: z.enum(['each', 'set', 'pair', 'pack', 'box']),
  vatCategory: z.enum(['standard', 'zero', 'exempt']),
  /** Option names, e.g. ["Colour", "Fabric"]. */
  attributes: z.array(z.string().trim().min(1).max(30)).min(1, 'Add at least one option, such as Colour').max(3),
  description: nullableText(500),
});

export const variantSchema = z.object({
  /** Values for each option, e.g. { Colour: "Grey", Fabric: "Suede" }. */
  values: z.record(z.string(), z.string().trim().min(1).max(40)),
  sku: z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .max(32)
    .regex(/^[A-Z0-9\-_.]+$/),
  priceCents: cents,
  costCents: cents.default(0),
  reorderLevelMilli: z.number().int().min(0).default(0),
  barcodes: z.array(barcodeSchema).max(5).default([]),
});

/* ── Customer orders, deposits and lay-by ───────────────────────────── */

export const ORDER_STATUSES = {
  open: 'Taking deposits',
  ready: 'Ready for collection or delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
} as const;
export type OrderStatus = keyof typeof ORDER_STATUSES;

export const orderSchema = z.object({
  branchId: id,
  customerId: id,
  dueDate: dateOnly.nullable().optional(),
  notes: nullableText(500),
  lines: z
    .array(z.object({ productId: id, qtyMilli: qty, unitPriceCents: cents.nullable().optional() }))
    .min(1, 'Add at least one item')
    .max(100),
  deposit: z
    .object({
      method: z.enum(NON_ACCOUNT_METHODS),
      currency: z.enum(CURRENCY_CODES as [string, ...string[]]).nullable().optional(),
      amountCents: z.number().int().positive().max(1_000_000_000_00),
      reference: nullableText(60),
    })
    .nullable()
    .optional(),
});
export type OrderInput = z.infer<typeof orderSchema>;

export const orderPaymentSchema = z.object({
  method: z.enum(NON_ACCOUNT_METHODS),
  currency: z.enum(CURRENCY_CODES as [string, ...string[]]).nullable().optional(),
  amountCents: z.number().int().positive('Enter an amount').max(1_000_000_000_00),
  reference: nullableText(60),
});

export const completeOrderSchema = z.object({
  clientRef: z.uuid(),
  /** Payments for the balance still owed (deposits already paid are applied automatically). */
  payments: z
    .array(
      z.object({
        method: z.enum([...NON_ACCOUNT_METHODS, 'account'] as [string, ...string[]]),
        currency: z.enum(CURRENCY_CODES as [string, ...string[]]),
        amountCents: z.number().int().positive().max(1_000_000_000_00),
        reference: nullableText(60),
      }),
    )
    .max(6)
    .default([]),
});

export const cancelOrderSchema = z.object({
  reason: z.string().trim().min(3, 'Say why the order is cancelled').max(200),
  /** How much of the deposits to give back; the rest is kept (forfeited). */
  refundCents: z.number().int().min(0).max(1_000_000_000_00),
  refundMethod: z.enum(NON_ACCOUNT_METHODS).nullable().optional(),
  reference: nullableText(60),
});

/* ── Deliveries ─────────────────────────────────────────────────────── */

export const DELIVERY_STATUSES = {
  scheduled: 'Scheduled',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Could not deliver',
  cancelled: 'Cancelled',
} as const;
export type DeliveryStatus = keyof typeof DELIVERY_STATUSES;
export const DELIVERY_SLOTS = { morning: 'Morning (8–12)', afternoon: 'Afternoon (12–4)', evening: 'Late (4–6)' } as const;

export const deliverySchema = z
  .object({
    branchId: id,
    orderId: id.nullable().optional(),
    saleId: id.nullable().optional(),
    address: z.string().trim().min(5, 'Enter the delivery address').max(300),
    contactName: nullableText(80),
    contactPhone: z.string().trim().min(6, 'Enter a phone number for the driver to call').max(20),
    scheduledDate: dateOnly,
    slot: z.enum(['morning', 'afternoon', 'evening']),
    driverName: nullableText(80),
    vehicle: nullableText(40),
    notes: nullableText(300),
  })
  .refine((v) => !!v.orderId !== !!v.saleId, { message: 'Link the delivery to an order or a sale', path: ['orderId'] });

export const deliveryUpdateSchema = z.object({
  status: z.enum(['scheduled', 'out_for_delivery', 'delivered', 'failed', 'cancelled']),
  scheduledDate: dateOnly.optional(),
  slot: z.enum(['morning', 'afternoon', 'evening']).optional(),
  driverName: nullableText(80),
  vehicle: nullableText(40),
  receivedBy: nullableText(80),
  note: nullableText(300),
});

/** Allowed delivery status changes. */
export const DELIVERY_FLOW: Record<DeliveryStatus, DeliveryStatus[]> = {
  scheduled: ['out_for_delivery', 'cancelled', 'scheduled'],
  out_for_delivery: ['delivered', 'failed'],
  failed: ['scheduled', 'cancelled'],
  delivered: [],
  cancelled: [],
};
